"use client";

import { ChevronDown } from "lucide-react";
import { CopyField } from "./copy-field";
import { ClaudeIcon } from "../ClientIcon";
import { mcpConfig } from "../../lib/mcp-config";

// // // //

export type McpConnectClient = {
    clientId: string;
    clientSecret: string;
};

/**
 * McpConnectInstructions
 * Claude MCP setup instructions. Claude is the only supported client today;
 * more MCP clients are coming soon.
 */
export function McpConnectInstructions(props: {
    client: McpConnectClient;
    enableCopy?: boolean;
}) {
    const { client, enableCopy = true } = props;

    return <ClaudeInstructions client={client} enableCopy={enableCopy} />;
}

function ClaudeInstructions({
    client,
    enableCopy = true,
}: {
    client: McpConnectClient;
    enableCopy?: boolean;
}) {
    return (
        <div className="flex flex-col gap-4">
            <ol className="flex flex-col gap-1.5 text-sm leading-relaxed text-muted-foreground">
                <Numbered n={1}>
                    In Claude, go to{" "}
                    <span className="text-foreground">
                        Settings → Connectors → Add custom connector
                    </span>
                    .
                </Numbered>
                <Numbered n={2}>
                    Fill in the fields below — expand{" "}
                    <span className="text-foreground">Advanced settings</span>{" "}
                    for the OAuth values — then click{" "}
                    <span className="text-foreground">Add</span>.
                </Numbered>
            </ol>

            <div>
                <div className="mb-2 flex items-center gap-2">
                    <ClaudeIcon className="size-4" />
                    <span className="text-xs font-medium text-muted-foreground">
                        Claude
                    </span>
                </div>

                <div className="dark rounded-lg border bg-card p-4 text-foreground">
                    <div className="mb-3 flex items-center gap-2">
                        <span className="text-sm font-semibold text-foreground">
                            Add custom connector
                        </span>
                        <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-secondary-foreground">
                            Beta
                        </span>
                    </div>

                    <div className="flex flex-col gap-3">
                        <ConnectorField
                            claudeLabel="Name"
                            value={mcpConfig.serverName}
                            mono={false}
                            enableCopy={enableCopy}
                        />
                        <ConnectorField
                            claudeLabel="Remote MCP server URL"
                            value={mcpConfig.serverUrl}
                            enableCopy={enableCopy}
                        />

                        <div className="flex items-center gap-2 pt-1 text-xs font-medium text-muted-foreground">
                            <ChevronDown className="size-3.5" />
                            Advanced settings
                        </div>

                        <ConnectorField
                            claudeLabel="OAuth Client ID"
                            value={client.clientId}
                            enableCopy={enableCopy}
                        />
                        <ConnectorField
                            claudeLabel="OAuth Client Secret"
                            value={client.clientSecret}
                            secret={enableCopy}
                            enableCopy={enableCopy}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

function ConnectorField({
    claudeLabel,
    value,
    mono = true,
    secret = false,
    info,
    enableCopy = true,
}: {
    claudeLabel: string;
    value: string;
    mono?: boolean;
    secret?: boolean;
    info?: string;
    enableCopy?: boolean;
}) {
    return (
        <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-foreground">
                {claudeLabel}
            </span>
            <CopyField
                label=""
                value={value}
                mono={mono}
                secret={secret}
                info={info}
                enableCopy={enableCopy}
            />
        </div>
    );
}

function Numbered({ n, children }: { n: number; children: React.ReactNode }) {
    return (
        <li className="flex gap-2.5">
            <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-secondary-foreground">
                {n}
            </span>
            <span>{children}</span>
        </li>
    );
}
